<template>
  <div class="desk-event-wrap">
    <div :class="['desk-event', timeCssClass]">
      <div class="top-row">
        <span class="when">
          <span class="d">{{ startDate }}<template v-if="endDate"> <span class="arrow">→</span> {{ endDate }}</template></span>
          <span class="t">{{ startTime }}<span class="arrow"> → </span>{{ endTime }}</span>
        </span>
        <span v-if="isRepeating" class="repeat-hint" title="Part of a repeating series">
          <i class="ion ion-md-repeat"></i>
        </span>
        <span v-if="hasTimezoneMismatch" class="tz-hint">
          <i class="ion ion-md-information-circle"></i>
          <span class="tz-hint-bubble">{{ browserTimeHint }}</span>
        </span>
        <a v-if="canManageEvent" href="#" class="edit-link ml-auto" @click="edit">edit</a>
      </div>
      <div class="daybar" :style="daybarStyle">
        <span class="tick" style="left: 25%"></span>
        <span class="tick" style="left: 50%"></span>
        <span class="tick" style="left: 75%"></span>
        <span class="seg" :style="segStyle"></span>
        <span v-if="isPresent" class="now-mark" :style="{ left: nowMarkLeftPct + '%' }"></span>
        <span v-if="endDate" class="continues">{{ extraDaysLabel }}</span>
      </div>
      <div class="bottom-row" v-if="employee">
        <div class="user">
          <app-avatar :src="employeeAvatarSrc" class="mr-2"></app-avatar>
          <div>
            <div>{{ employee.name }}</div>
            <div>{{ employee.email }}</div>
          </div>
        </div>
      </div>
    </div>
    <button v-if="!isPast" type="button" class="add-after" @click="addAfter" title="Add another booking">
      <i class="ion ion-md-add"></i>
      Add another booking
    </button>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { mapGetters, mapState } from 'vuex'
import AppAvatar from '@/components/shared/AppAvatar'
import { getAvatarSrc, isLeadingMonth } from '@/utils/Helpers'
import { isSameTimezone, SERVER_DATE_TIME_FORMAT, timezoneCity } from '@/components/desk/utils'
import { getIconUrl } from '@/components/leaflet/Icon'
import { POINT_ICONS } from '@/store/modules/point'
import { canManageEmployeeDeskEvents } from '@/utils/DeskEventOwnership'

const MINUTES_PER_DAY = 24 * 60
// white gap between the current day and the day counter, and the narrowest stub of the current
// day we still draw next to the counter — both in px, mirrored by the .daybar styles below
const CHIP_GAP_PX = 3
const MIN_SEG_PX = 14

function minutesOfDay(d) {
  return d.hour() * 60 + d.minute()
}

export default {
  name: 'DeskEvent',
  components: { AppAvatar },
  props: {
    event: {
      type: Object,
      required: true
    }
  },
  computed: {
    ...mapGetters('employee', ['getEmployee']),
    ...mapGetters('rbac', ['canBookForOthers']),
    ...mapGetters('desk', ['getDeskTimezone']),
    ...mapState('auth', ['user']),
    employee() {
      return this.getEmployee(this.event.employee_id)
    },
    iconSrc() {
      return getIconUrl(POINT_ICONS.EMPLOYEE)
    },
    employeeAvatarSrc() {
      return getAvatarSrc(this.employee.avatar_id, this.employee.avatar_updated_at, this.iconSrc)
    },
    isRepeating() {
      return !!this.event.set_id
    },
    buildingTimezone() {
      return this.getDeskTimezone(this.event.desk_id) || dayjs.tz.guess()
    },
    browserTimezone() {
      return dayjs.tz.guess()
    },
    hasTimezoneMismatch() {
      return !isSameTimezone(this.buildingTimezone, this.browserTimezone)
    },
    start() {
      // the stored string carries its own offset — parse it plainly, then re-view in the building's
      // zone; dayjs.tz(str, format, zone) must not be used here, it double-applies the offset
      return dayjs(this.event.start, SERVER_DATE_TIME_FORMAT).tz(this.buildingTimezone)
    },
    end() {
      return dayjs(this.event.end, SERVER_DATE_TIME_FORMAT).tz(this.buildingTimezone)
    },
    browserTimeHint() {
      const city = timezoneCity(this.buildingTimezone)
      const start = this.start.tz(this.browserTimezone).format('HH:mm')
      const end = this.end.tz(this.browserTimezone).format('HH:mm')
      return `${city} time - yours: ${start}–${end}`
    },
    localeMonthDayFormat() {
      return isLeadingMonth() ? 'MM/DD' : 'DD/MM'
    },
    startDate() {
      return this.start.format(this.localeMonthDayFormat)
    },
    startTime() {
      return this.start.format('HH:mm')
    },
    endDate() {
      if (this.start.isBefore(this.end, 'date')) {
        return this.end.format(this.localeMonthDayFormat)
      }
      return false
    },
    endTime() {
      return this.end.format('HH:mm')
    },
    // number of extra calendar days the booking runs into beyond its start day — also covers a
    // plain overnight booking that just crosses one midnight (e.g. 21:00–05:00)
    extraDays() {
      return this.end.startOf('day').diff(this.start.startOf('day'), 'day')
    },
    extraDaysLabel() {
      return this.extraDays === 1 ? '+1 day' : `+${this.extraDays} days`
    },
    // the day counter is sized in `ch` units off its own monospace font rather than measured in
    // JS: on .daybar --chip-w resolves to min(quarter of the bar, label width + padding), and both
    // the counter and the day segment read it, so they always meet exactly
    daybarStyle() {
      return { '--chip-ch': String(this.extraDaysLabel.length) }
    },
    // the day-strip always shows the *start* day's 0:00-24:00 span; a booking that runs past
    // midnight stops at the left edge of the day counter instead of trying to draw a bar
    // proportional to a multi-day range
    segStyle() {
      if (this.endDate) {
        // a booking starting late in the evening would leave no room next to the counter — the
        // min() floor keeps a readable stub of the current day visible
        return {
          left: `min(${this.segLeftPct}%, calc(100% - var(--chip-w) - ${MIN_SEG_PX}px))`,
          right: `calc(var(--chip-w) + ${CHIP_GAP_PX}px)`
        }
      }
      return { left: `${this.segLeftPct}%`, width: `${this.segWidthPct}%` }
    },
    segLeftPct() {
      return (minutesOfDay(this.start) / MINUTES_PER_DAY) * 100
    },
    segWidthPct() {
      return ((minutesOfDay(this.end) - minutesOfDay(this.start)) / MINUTES_PER_DAY) * 100
    },
    nowMarkLeftPct() {
      return (minutesOfDay(dayjs().tz(this.buildingTimezone)) / MINUTES_PER_DAY) * 100
    },
    isPast() {
      return this.end.isBefore(dayjs())
    },
    isPresent() {
      return !this.isPast && this.start.isBefore(dayjs())
    },
    timeCssClass() {
      if (this.isPast) return 'past'
      if (this.isPresent) return 'present'
      return 'future'
    },
    canManageEvent() {
      return canManageEmployeeDeskEvents(this.employee, this.user, this.canBookForOthers)
    }
  },
  methods: {
    addAfter() {
      this.$emit('addAfter')
    },
    edit() {
      this.$emit('edit')
    }
  }
}
</script>

<style scoped lang="scss">
.desk-event-wrap {
  margin-bottom: 20px;
}

.desk-event {
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.09);
  border-radius: 5px;
  padding: 10px 12px;

  &.past {
    opacity: 0.55;
  }

  &.present {
    border-color: rgba(30, 112, 205, 0.35);
    box-shadow: 0 2px 8px rgba(30, 112, 205, 0.12);
  }
}

.top-row {
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;

  .when {
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 15px;
    font-weight: 500;
    color: #2f3338;
    white-space: nowrap;

    .t {
      font-family: ui-monospace, 'SF Mono', Consolas, monospace;
      font-size: 14px;
      font-weight: 500;
      letter-spacing: -0.01em;
    }

    .arrow {
      color: rgba(0, 0, 0, 0.35);
    }
  }

  .edit-link {
    flex-shrink: 0;
    margin-left: auto;
    font-size: 13.5px;
    color: #1e70cd;
    border-bottom: 1px dashed #1e70cd;
    line-height: 15px;
  }
}

.repeat-hint {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 13px;
  color: #1e70cd;
}

.tz-hint {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  font-size: 14px;
  color: #999;
  cursor: default;

  .tz-hint-bubble {
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    margin-bottom: 6px;
    padding: 4px 8px;
    background: #333;
    color: #fff;
    font-size: 11px;
    white-space: nowrap;
    border-radius: 4px;
    z-index: 20;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.15s;
  }

  &:hover .tz-hint-bubble {
    opacity: 1;
  }
}

.daybar {
  // the day counter is sized off its own monospace font: 1ch is one glyph, so the label needs
  // exactly --chip-ch of them plus the side padding — capped at a quarter of the bar. The font
  // is set here, on the bar, so `ch` resolves the same for the counter and for the day segment.
  --chip-pad: 7px;
  --chip-w: min(25%, calc(var(--chip-ch, 6) * 1ch + var(--chip-pad) * 2));

  position: relative;
  // the counter and the now-mark stack against each other, not against the page: without an
  // isolated context their z-index would lift them over the fixed "+" and delete buttons, which
  // sit at z-index auto in the root context
  isolation: isolate;
  height: 14px;
  border-radius: 7px;
  background: rgba(0, 0, 0, 0.065);
  font: 600 11.5px/1 ui-monospace, 'SF Mono', Consolas, monospace;

  .tick {
    position: absolute;
    top: 3px;
    bottom: 3px;
    width: 1px;
    background: rgba(0, 0, 0, 0.13);
  }

  .seg {
    position: absolute;
    top: 0;
    bottom: 0;
    border-radius: 7px;
    background: #1e70cd;
  }

  .now-mark {
    position: absolute;
    top: -3px;
    bottom: -3px;
    width: 2px;
    border-radius: 1px;
    background: #0d3f78;
    z-index: 3;
  }

  // the counter butts against the current day with a 3px white gap: the day reads as a closed
  // stretch, the counter as the next one starting and cut off by the edge of the bar
  .continues {
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    width: var(--chip-w);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 var(--chip-pad);
    border-radius: 7px 0 0 7px;
    background: #0d3f78;
    color: #fff;
    letter-spacing: -0.01em;
    white-space: nowrap;
    overflow: hidden;
    z-index: 2;
  }
}

.desk-event.past .daybar {
  .seg {
    background: rgba(0, 0, 0, 0.32);
  }

  .continues {
    background: rgba(0, 0, 0, 0.45);
  }
}

.bottom-row .user {
  display: flex;
  align-items: center;
  overflow: hidden;
}

.add-after {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100%;
  height: 24px;
  margin-top: 6px;
  padding: 0;
  border: 1px dashed transparent;
  border-radius: 8px;
  background: transparent;
  color: transparent;
  font-size: 12.5px;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s, background-color 0.15s;

  .ion {
    font-size: 14px;
  }
}

.desk-event-wrap:hover .add-after {
  border-color: #1e70cd;
  color: #1e70cd;
  background: rgba(30, 112, 205, 0.06);
}
</style>
